import AddIcon from "@mui/icons-material/AddCircleOutlineRounded";
import { default as DeleteIcon } from "@mui/icons-material/HighlightOffRounded";
import { useEffect, useRef, useState } from "react";
import { useFieldArray, useFormContext } from "react-hook-form";
import {
  MuiBox,
  MuiButtonTertiary,
  MuiTypography,
  WarningConfirmationModal,
} from "../../../../components";
import {
  MODULE_PERMISSIONS,
  PAGE_IDS,
  TOAST_AUTO_HIDE_DURATION,
} from "../../../../helpers/constants";
import {
  getNameByIdFromList,
  isNotEmpty,
  replaceHashWithValue,
} from "../../../../helpers/utils";
import { useSiteCore } from "../../../../hooks/useSiteCore";
import {
  BillingConfigurationProgramTemplateItem,
  CreateClientFormKey as ProgramTemplateFormKey,
} from "../../modelsAndSchemas/Models";
import { billingAndPurchaseOrderKeys } from "../../siteCore/SiteCoreKeys";
import { ProgramTemplateWrapper } from "./BillingProgramTemplate.style";

import { useDispatch, useSelector } from "react-redux";
import { BillingTypeConfigureTable } from "../../../../components/billingTypeConfigureTable/BillingTypeConfigureTable";
import {
  CURRENCY_FORMAT_MAP,
  CurrencyFormat,
} from "../../../../components/textFieldCurrency/CurrencyUtils";

import { useNavigationSiteCore } from "../../../../hooks/useNavigationSiteCore";
import { usePermissions } from "../../../../hooks/usePermissions";
import { currencyListSelector } from "../../../../redux/slices/appState/appStateSelector";
import { toggleToast } from "../../../../redux/slices/appState/appStateSlice";
import {
  createClientSelector,
  editClientSelector,
} from "../../../../redux/slices/client/createClientSelector";
import { getBillingConfigurationDetailsFormKeys } from "../../helper";
import { BillingTypeField } from "./billingTypeField/BillingTypeField";

interface ProgramTemplateProps {
  configIndex: number;
  onConfigOrProgramTemplateDeleted?: (isDeleted: boolean) => void;
  lobId?: string;
  forceUpdate: () => void;
}

export interface ConfirmationChangeState {
  showConfirmation: boolean;
  indexNumber: number;
}

export interface ConfirmationCallback {
  handleConfirmationStatusTrue: () => void;
  handleConfirmationStatusFalse: () => void;
}

const BillingProgramTemplate = ({
  configIndex,
  onConfigOrProgramTemplateDeleted,
  lobId,
  forceUpdate,
}: ProgramTemplateProps) => {
  const [showDeleteConfirmationModal, setShowDeleteConfirmationModal] =
    useState(false);
  const [
    warningOnProgramTemplateOrBillingState,
    setWarningOnProgramTemplateOrBillingState,
  ] = useState<ConfirmationChangeState>({
    showConfirmation: false,
    indexNumber: 0,
  });
  const [isErrorFromProgramTemplate, setIsErrorFromProgramTemplate] =
    useState(false);
  const [billingCountryId, setBillingCountryId] = useState("");
  const { commonLabels, fetchLabel } = useSiteCore();
  const { commonARIALabel } = useNavigationSiteCore();
  const { hasModulePermission } = usePermissions();

  const indexRef = useRef<number>();
  const dispatch = useDispatch();
  const { control, getValues } = useFormContext();
  const { id: clientId } = useSelector(createClientSelector);
  const billingTypeFieldRefList = useRef<Array<ConfirmationCallback>>([]);
  const { isEdit: isEditClient } = useSelector(editClientSelector);

  const {
    fields: programTemplateFields,
    append: appendProgramTemplate,
    remove: removeProgramTemplate,
  } = useFieldArray({
    control,
    name: `${ProgramTemplateFormKey.configurations}.${configIndex}.${ProgramTemplateFormKey.programTemplateConfigurations}`,
    keyName: "templateId",
  });

  const {
    addProgramTemplateTitle,
    billingDetailsAndPOTitle,
    addProgramTemplate,
    programTemplateNumber,
    deleteProgramTemplateActionText,
    programTemplateDeletedMessage,
    areYouSureYouWantToChangeTheBillingType,
    areYouSureYouWantToChangeTheProgramTemplate,
  } = billingAndPurchaseOrderKeys;

  const pageId = PAGE_IDS.CREATE_PROGRAM_TEMPLATE;

  const currenciesList = useSelector(currencyListSelector);

  useEffect(() => {
    if (currenciesList?.length) {
      setBillingCountryId(currenciesList[0].id);
    }
  }, [currenciesList]);

  const emptyBillingConfigProgramTemplateItem = (
    defaultCurrency: string,
  ): BillingConfigurationProgramTemplateItem => {
    return {
      id: "",
      programTemplateId: "",
      billingTypeId: "",
      billingTriggerId: "",
      currencyId: defaultCurrency,
      fee: "",
    };
  };

  const addTemplate = () => {
    appendProgramTemplate(
      emptyBillingConfigProgramTemplateItem(billingCountryId),
    );
  };

  const deleteTemplate = (index: number) => {
    indexRef.current = index;
    setShowDeleteConfirmationModal(true);
  };

  const handleModalClose = (isFromDeleteProgram: boolean = false) => {
    if (!isFromDeleteProgram) {
      billingTypeFieldRefList.current?.[
        warningOnProgramTemplateOrBillingState.indexNumber
      ].handleConfirmationStatusFalse();
    }

    setShowDeleteConfirmationModal(false);
    setWarningOnProgramTemplateOrBillingState({
      showConfirmation: false,
      indexNumber: 0,
    });
    setIsErrorFromProgramTemplate(false);
  };

  const handleDeleteConfiguration = () => {
    if (isNotEmpty(indexRef.current)) {
      removeProgramTemplate(indexRef.current);
      handleModalClose(true);
      dispatch(
        toggleToast({
          open: true,
          message: fetchLabel(
            billingDetailsAndPOTitle,
            programTemplateDeletedMessage,
          ),
          type: "success",
          autoHideDuration: TOAST_AUTO_HIDE_DURATION,
        }),
      );
      indexRef.current = undefined;
      onConfigOrProgramTemplateDeleted?.(true);
    }
  };

  const handleProgramTemplateOrBillingChange = () => {
    billingTypeFieldRefList.current?.[
      warningOnProgramTemplateOrBillingState.indexNumber
    ].handleConfirmationStatusTrue();
    setWarningOnProgramTemplateOrBillingState({
      showConfirmation: false,
      indexNumber: 0,
    });
    setTimeout(() => {
      forceUpdate();
    }, 200);
  };

  return (
    <ProgramTemplateWrapper>
      <form>
        <MuiBox>
          <MuiBox className="program-template-wrapper">
            {programTemplateFields.map((template, templateIndex) => {
              const { currencyId } =
                getValues().configurations[configIndex]
                  .programTemplateConfigurations[templateIndex];

              const currencyName = getNameByIdFromList(
                currenciesList,
                currencyId,
              );

              const currencyFormat: CurrencyFormat =
                CURRENCY_FORMAT_MAP[currencyName];

              const formIdPrefix = `${ProgramTemplateFormKey.configurations}.${configIndex}.${ProgramTemplateFormKey.programTemplateConfigurations}.${templateIndex}`;
              const programTemplateId = getValues(
                `${formIdPrefix}.${ProgramTemplateFormKey.programTemplateId}`,
              );

              return (
                <MuiBox key={template.templateId} className="program-template">
                  <MuiBox className="program-template-header">
                    <MuiTypography
                      variant="h2Bold"
                      data-testId={`${pageId}-${ProgramTemplateFormKey.header}`}
                    >
                      {replaceHashWithValue(
                        fetchLabel(
                          billingDetailsAndPOTitle,
                          programTemplateNumber,
                        ),
                        String(templateIndex + 1),
                      )}
                    </MuiTypography>
                    {hasModulePermission(
                      MODULE_PERMISSIONS.DELETE_CLIENT_BILLING_CONFIG,
                    ) && (
                      <MuiButtonTertiary
                        onClick={() => deleteTemplate(templateIndex)}
                        data-testId={`${pageId}-${ProgramTemplateFormKey.deleteTemplate}`}
                        aria-label={commonARIALabel.delete}
                      >
                        <DeleteIcon className="delete-icon" />
                        {commonLabels.delete}
                      </MuiButtonTertiary>
                    )}
                  </MuiBox>

                  <BillingTypeField
                    ref={(ref: ConfirmationCallback) => {
                      billingTypeFieldRefList.current[templateIndex] = ref;
                    }}
                    formIdPrefix={formIdPrefix}
                    currencyFormat={currencyFormat}
                    currencyId={currencyId}
                    defaultUserCurrency={billingCountryId}
                    currenciesList={currenciesList || []}
                    forceUpdate={forceUpdate}
                    templateIndex={templateIndex}
                    setShowWarningOnProgramBillingChange={
                      setWarningOnProgramTemplateOrBillingState
                    }
                    setIsErrorFromProgramTemplate={
                      setIsErrorFromProgramTemplate
                    }
                    isErrorFromProgramTemplate={isErrorFromProgramTemplate}
                    lobId={lobId}
                  />

                  <BillingTypeConfigureTable
                    pageId={pageId}
                    currencyName={currencyName}
                    currencyFormat={currencyFormat}
                    currencyId={currencyId}
                    forceUpdate={forceUpdate}
                    programTemplateId={programTemplateId}
                    billingConfigurationDetailsFormKeys={getBillingConfigurationDetailsFormKeys(
                      formIdPrefix,
                    )}
                    clientId={clientId}
                    isInEditMode={isEditClient}
                  />
                </MuiBox>
              );
            })}
          </MuiBox>
          <MuiBox className="add-program-template">
            <MuiButtonTertiary
              onClick={addTemplate}
              data-testId={`${pageId}-${ProgramTemplateFormKey.addProgramTemplate}`}
            >
              <AddIcon className="add-icon" />
              {fetchLabel(billingDetailsAndPOTitle, addProgramTemplate)}
            </MuiButtonTertiary>
          </MuiBox>
        </MuiBox>
        {showDeleteConfirmationModal && (
          <WarningConfirmationModal
            show={showDeleteConfirmationModal}
            modalTitle={commonLabels.confirmation}
            onClose={handleModalClose}
            onPrimaryButtonClick={handleDeleteConfiguration}
            labelContent={fetchLabel(
              billingDetailsAndPOTitle,
              deleteProgramTemplateActionText,
            )}
          />
        )}
        {warningOnProgramTemplateOrBillingState && (
          <WarningConfirmationModal
            show={warningOnProgramTemplateOrBillingState.showConfirmation}
            modalTitle={commonLabels.confirmation}
            secondaryButtonTitle={commonLabels.no}
            onClose={() => handleModalClose(false)}
            onPrimaryButtonClick={handleProgramTemplateOrBillingChange}
            labelContent={fetchLabel(
              addProgramTemplateTitle,
              isErrorFromProgramTemplate
                ? areYouSureYouWantToChangeTheProgramTemplate
                : areYouSureYouWantToChangeTheBillingType,
            )}
          />
        )}
      </form>
    </ProgramTemplateWrapper>
  );
};

export default BillingProgramTemplate;
